/**
 * Authentication Service
 * Orchestrates authentication flows using smaller modules
 * This is the main API that replaces Amplify Auth
 */

// Remaining Cognito direct calls
import {
  getAWSCredentials  // Keep: Used for S3 access (identity pool)
} from './cognitoClient';
import { API } from '../api/apiClient';
import {
  storeTokens,
  getStoredTokens,
  clearTokens,
  storeUsername,
  isTokenExpired,
  getTimeUntilExpiry
} from './tokenManager';
import { fetchUser } from './userManager';
import eventEmitter from './eventEmitter';
import { logSecurityEvent, SecurityEventType } from './securityMonitor';

/**
 * Authentication service state
 */
let currentUser = null;
let currentTokens = null;
let tokenRefreshTimer = null;

/**
 * Sign in with Account ID, email, and password
 * Calls Auth API login endpoint which handles authentication and audit logging
 * @param {string} accountId - 12-digit Account ID
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @param {string} tenantId - Optional tenant ID
 * @returns {Promise<Object>} { user, tokens } or { challengeName, session }
 */
export const signIn = async (accountId, email, password, tenantId = null) => {
  try {
    // Call Auth API login endpoint (which logs to audit trail)
    const response = await API.post('auth', '/auth/login', {
      body: {
        accountId,
        email,
        password,
        ...(tenantId && { tenantId })
      }
    });

    // Handle MFA or other challenges
    if (response.challengeName) {
      return {
        challengeName: response.challengeName,
        session: response.session,
        challengeParameters: response.challengeParameters
      };
    }

    // Extract and store tokens
    const tokens = {
      accessToken: response.accessToken,
      idToken: response.idToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn
    };

    await storeTokens(tokens);
    storeUsername(accountId);  // Store Account ID as username
    currentTokens = tokens;

    // Fetch and build user object
    currentUser = await fetchUser(tokens.accessToken, tokens);

    // Start token refresh timer
    startTokenRefreshTimer();

    // Emit sign-in event
    eventEmitter.emit('signIn', { user: currentUser });

    // Log security event (local only - server already logged via API)
    logSecurityEvent(SecurityEventType.LOGIN_SUCCESS, { accountId, email });

    return { user: currentUser, tokens };
  } catch (error) {
    eventEmitter.emit('signIn_failure', { error });

    // Log security event (local only - server already logged via API)
    logSecurityEvent(SecurityEventType.LOGIN_FAILURE, {
      accountId,
      email,
      error: error.message
    });

    throw error;
  }
};

/**
 * Confirm MFA code
 * Calls Auth API verify-mfa endpoint which verifies code and logs audit event
 * @param {Object} challengeResult - Result from signIn with challenge
 * @param {string} mfaCode - MFA code from user
 * @returns {Promise<Object>} { user, tokens }
 */
export const confirmMfa = async (challengeResult, mfaCode) => {
  try {
    const response = await API.post('auth', '/auth/verify-mfa', {
      body: {
        session: challengeResult.session,
        challengeName: challengeResult.challengeName,
        mfaCode,
        accountId: challengeResult.accountId
      }
    });

    // Extract and store tokens
    const tokens = {
      accessToken: response.accessToken,
      idToken: response.idToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn
    };

    await storeTokens(tokens);
    currentTokens = tokens;

    // Fetch and build user object
    currentUser = await fetchUser(tokens.accessToken, tokens);

    // Start token refresh timer
    startTokenRefreshTimer();

    // Emit sign-in event
    eventEmitter.emit('signIn', { user: currentUser });

    // Log security event (local only - server already logged via API)
    logSecurityEvent(SecurityEventType.LOGIN_SUCCESS, { username: currentUser.username });

    return { user: currentUser, tokens };
  } catch (error) {
    eventEmitter.emit('signIn_failure', { error });

    // Log security event (local only - server already logged via API)
    logSecurityEvent(SecurityEventType.LOGIN_FAILURE, {
      error: error.message
    });

    throw error;
  }
};

/**
 * Complete NEW_PASSWORD_REQUIRED challenge
 * Calls Auth API complete-new-password endpoint which sets password and logs audit event
 * @param {Object} challengeResult - Result from signIn with challenge
 * @param {string} newPassword - New password
 * @param {string} accountId - 12-digit Account ID
 * @returns {Promise<Object>} { user, tokens }
 */
export const completeNewPasswordChallenge = async (challengeResult, newPassword, accountId) => {
  try {
    const response = await API.post('auth', '/auth/complete-new-password', {
      body: {
        session: challengeResult.session,
        accountId,
        newPassword
      }
    });

    // Extract and store tokens (Auth API returns capitalized keys from Cognito)
    const tokens = {
      accessToken: response.AccessToken,
      idToken: response.IdToken,
      refreshToken: response.RefreshToken,
      expiresIn: response.ExpiresIn
    };

    await storeTokens(tokens);
    storeUsername(accountId);
    currentTokens = tokens;

    // Fetch and build user object
    currentUser = await fetchUser(tokens.accessToken, tokens);

    // Start token refresh timer
    startTokenRefreshTimer();

    // Emit sign-in event
    eventEmitter.emit('signIn', { user: currentUser });

    // Log security event (local only - server already logged via API)
    logSecurityEvent(SecurityEventType.LOGIN_SUCCESS, { username: accountId });

    return { user: currentUser, tokens };
  } catch (error) {
    eventEmitter.emit('signIn_failure', { error });

    // Log security event (local only - server already logged via API)
    logSecurityEvent(SecurityEventType.LOGIN_FAILURE, {
      username: accountId,
      error: error.message
    });

    throw error;
  }
};

/**
 * Sign out current user
 * Calls Auth API logout endpoint which invalidates sessions and logs audit event
 */
export const signOut = async () => {
  try {
    if (currentTokens?.accessToken) {
      // Call Auth API logout endpoint (which logs to audit trail)
      await API.post('auth', '/auth/logout', {
        headers: {
          'Authorization': `Bearer ${currentTokens.accessToken}`
        }
      });
    }
  } catch (error) {
    console.error('Error during logout API call:', error);
    // Continue with local cleanup even if API call fails
  } finally {
    // Stop token refresh timer
    stopTokenRefreshTimer();

    // Clear state
    currentUser = null;
    currentTokens = null;
    clearTokens();

    // Emit sign-out event
    eventEmitter.emit('signOut', {});

    // Log security event (local only - server already logged via API)
    logSecurityEvent(SecurityEventType.LOGOUT);
  }
};

/**
 * Get current authenticated user
 * @returns {Promise<Object>} User object
 * @throws {Error} If no authenticated user
 */
export const currentAuthenticatedUser = async () => {
  // Return cached user if available
  if (currentUser) {
    return currentUser;
  }

  // Try to restore from storage
  const storedTokens = await getStoredTokens();
  if (!storedTokens) {
    throw new Error('No authenticated user');
  }

  // Check if token is expired
  if (isTokenExpired(storedTokens.accessToken)) {
    // Try to refresh if we have a refresh token
    if (storedTokens.refreshToken) {
      try {
        // Set currentTokens before refresh so refreshTokens() can access it
        currentTokens = storedTokens;
        await refreshTokens();

        // Fetch user with refreshed tokens
        currentUser = await fetchUser(currentTokens.accessToken, currentTokens);
        return currentUser;
      } catch (refreshError) {
        console.error('Token refresh failed during restoration:', refreshError);
        await signOut();
        throw new Error('Token expired');
      }
    } else {
      await signOut();
      throw new Error('Token expired');
    }
  }

  // Restore user
  try {
    currentTokens = storedTokens;
    currentUser = await fetchUser(storedTokens.accessToken, storedTokens);

    // Start token refresh timer
    startTokenRefreshTimer();

    return currentUser;
  } catch (error) {
    // Token is invalid
    await signOut();
    throw new Error('No authenticated user');
  }
};

/**
 * Sign up new user
 * Calls Auth API signup endpoint which handles user creation and audit logging
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @param {string} handle - User's handle (e.g., @username)
 * @param {string} tenantId - Optional tenant ID
 * @returns {Promise<Object>} { accountId, userId, temporaryPassword (if auto-generated) }
 */
export const signUp = async (email, password, handle, tenantId = null) => {
  const response = await API.post('auth', '/auth/signup', {
    body: {
      email,
      password,
      handle,
      ...(tenantId && { tenantId })
    }
  });
  eventEmitter.emit('signUp', { email, accountId: response.accountId });
  return response;
};

/**
 * Confirm sign up with verification code
 * Calls Auth API verify-email endpoint which confirms signup and logs audit event
 * @param {string} accountId - 12-digit Account ID
 * @param {string} code - Verification code from email
 */
export const confirmSignUp = async (accountId, code) => {
  await API.post('auth', '/auth/verify-email', {
    body: {
      accountId,
      code
    }
  });
  eventEmitter.emit('autoSignIn', { username: accountId });
};

/**
 * Initiate forgot password flow
 * Calls Auth API forgot-password endpoint which sends code and logs audit event
 * @param {string} accountId - 12-digit Account ID
 * @param {string} email - User's email
 */
export const forgotPassword = async (accountId, email) => {
  await API.post('auth', '/auth/forgot-password', {
    body: {
      accountId,
      email
    }
  });
};

/**
 * Confirm forgot password with code
 * Calls Auth API reset-password endpoint which resets password and logs audit event
 * @param {string} accountId - 12-digit Account ID
 * @param {string} code - Verification code from email
 * @param {string} newPassword - New password
 */
export const confirmForgotPassword = async (accountId, code, newPassword) => {
  await API.post('auth', '/auth/reset-password', {
    body: {
      accountId,
      code,
      newPassword
    }
  });
};

/**
 * Get current access token
 * @returns {Promise<string>} Access token
 * @throws {Error} If no access token available
 */
export const getAccessToken = async () => {
  // Return cached token if available
  if (currentTokens?.accessToken) {
    return currentTokens.accessToken;
  }

  // Try to restore from storage
  const storedTokens = await getStoredTokens();
  if (!storedTokens?.accessToken) {
    throw new Error('No access token available');
  }

  // Check if token is expired
  if (isTokenExpired(storedTokens.accessToken)) {
    // Try to refresh if we have a refresh token
    if (storedTokens.refreshToken) {
      currentTokens = storedTokens;
      await refreshTokens();
      return currentTokens.accessToken;
    } else {
      throw new Error('Access token expired');
    }
  }

  currentTokens = storedTokens;
  return storedTokens.accessToken;
};

/**
 * Get AWS credentials for accessing other AWS services
 * @returns {Promise<Object>} AWS credentials
 */
export const getCredentials = async () => {
  if (!currentTokens?.idToken) {
    throw new Error('No ID token available');
  }
  return await getAWSCredentials(currentTokens.idToken);
};

/**
 * Subscribe to authentication events
 * @param {string} event - Event name (signIn, signOut, signUp, etc.)
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const on = (event, callback) => {
  return eventEmitter.on(event, callback);
};

/**
 * Refresh current user's tokens
 * Calls Auth API refresh-token endpoint which refreshes tokens and logs audit event
 * @returns {Promise<Object>} New tokens
 * @throws {Error} If no refresh token available
 */
export const refreshTokens = async () => {
  if (!currentTokens?.refreshToken) {
    // Try to get from storage
    const storedTokens = await getStoredTokens();
    if (!storedTokens?.refreshToken) {
      throw new Error('No refresh token available');
    }
    currentTokens = storedTokens;
  }

  try {
    const response = await API.post('auth', '/auth/refresh-token', {
      body: {
        refreshToken: currentTokens.refreshToken
      }
    });

    // Extract new tokens
    const newTokens = {
      accessToken: response.accessToken,
      idToken: response.idToken,
      refreshToken: currentTokens.refreshToken, // Refresh token is not renewed
      expiresIn: response.expiresIn
    };

    // Store new tokens
    await storeTokens(newTokens);
    currentTokens = newTokens;

    // Restart token refresh timer
    startTokenRefreshTimer();

    console.log('Tokens refreshed successfully');
    eventEmitter.emit('tokenRefresh', { tokens: newTokens });

    // Log security event (local only - server already logged via API)
    logSecurityEvent(SecurityEventType.TOKEN_REFRESH_SUCCESS);

    return newTokens;
  } catch (error) {
    console.error('Token refresh failed:', error);

    // Log security event (local only - server already logged via API)
    logSecurityEvent(SecurityEventType.TOKEN_REFRESH_FAILURE, {
      error: error.message
    });

    throw error;
  }
};

/**
 * Start automatic token refresh timer
 * Refreshes tokens 5 minutes before expiry
 */
const startTokenRefreshTimer = () => {
  // Clear existing timer
  stopTokenRefreshTimer();

  if (!currentTokens?.accessToken) {
    return;
  }

  // Calculate when to refresh (5 minutes before expiry)
  const timeUntilExpiry = getTimeUntilExpiry(currentTokens.accessToken);
  const fiveMinutes = 5 * 60 * 1000;
  const refreshDelay = Math.max(0, timeUntilExpiry - fiveMinutes);

  console.log(`Token refresh scheduled in ${Math.round(refreshDelay / 1000)} seconds`);

  tokenRefreshTimer = setTimeout(async () => {
    try {
      console.log('Auto-refreshing tokens...');
      await refreshTokens();
    } catch (error) {
      console.error('Auto token refresh failed:', error);
      // Sign out if refresh fails
      await signOut();
    }
  }, refreshDelay);
};

/**
 * Stop automatic token refresh timer
 */
const stopTokenRefreshTimer = () => {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
};

/**
 * Default export for convenience
 */
export default {
  signIn,
  signOut,
  currentAuthenticatedUser,
  signUp,
  confirmSignUp,
  forgotPassword,
  confirmForgotPassword,
  getAccessToken,
  getCredentials,
  refreshTokens,
  on
};
